import {
  useState,
  useCallback,
  useEffect,
  useContext,
  useLayoutEffect,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import cn from 'classnames';
import Cookies from 'js-cookie';
import { useMsal } from '@azure/msal-react';
// import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { AuthContext } from '@providers/Auth';

import LoginModal from '@components/LoginModal';
import ForgotPasswordModal from '@components/ForgotPasswordModal';
import ResetPasswordModal from '@components/ResetPasswordModal';

import { getToken } from '@helpers/utils';

import WhiteshieldLogoWhite from '@assets/whiteshield_logo-white.png';
import WhiteshieldLogo from '@assets/whiteshield_logo.png';
import BackgroundDark from '@assets/login-background.jpg';
import BackgroundLight from '@assets/background-light.jpg';
import './Login.scss';

const infoText =
  'From trends, occupation, abilities, experience to education and work activity';
const descriptionText = 'Labour market insights at your fingertips';

const Login = () => {
  const title = process.env.REACT_APP_TITLE || '';
  const darkMode = Cookies.get('darkMode') === 'true';
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [passwordResetToken, setPasswordResetToken] = useState<string>('');
  const [passwordResetEmail, setPasswordResetEmail] = useState<string>('');
  const [showForgotPasswordModal, setShowForgotPasswordModal] =
    useState<boolean>(false);
  const [showResetPasswordModal, setShowResetPasswordModal] =
    useState<boolean>(false);
  const [animation, setAnimation] = useState<boolean>(false);

  const { setIsLoggedIn } = useContext(AuthContext);

  const { accounts } = useMsal();

  let navigate = useNavigate();
  let location = useLocation();

  const params = new URLSearchParams(location.search);
  const isForgetPassword = params.get('forgetPassword');
  const token = params.get('token');
  const email = params.get('email');

  useEffect(() => {
    if (isForgetPassword) {
      setShowForgotPasswordModal(true);
    }
  }, [isForgetPassword]);

  const handleOpenLoginModal = useCallback(() => {
    setShowLoginModal(true);
  }, []);

  const handleCloseLoginModal = useCallback(() => {
    setShowLoginModal(false);
  }, []);

  const handleOpenForgotPasswordModal = useCallback(() => {
    setShowForgotPasswordModal(true);
  }, []);

  const handleCloseForgotPasswordModal = useCallback(() => {
    setShowForgotPasswordModal(false);
  }, []);

  const handleOpenResetPasswordModal = useCallback(() => {
    setShowResetPasswordModal(true);
  }, []);

  const handleCloseResetPasswordModal = useCallback(() => {
    setShowResetPasswordModal(false);
  }, []);

  const handleForgotPasswordClick = useCallback(() => {
    handleCloseLoginModal();
    handleOpenForgotPasswordModal();
  }, [handleCloseLoginModal, handleOpenForgotPasswordModal]);

  useEffect(() => {
    const token = getToken();
    if (token) {
      setIsLoggedIn(true);
      navigate('/dashboard');
    }
  }, [navigate, setIsLoggedIn, accounts]);

  useEffect(() => {
    if (token && email && !isForgetPassword) {
      setPasswordResetToken(token);
      setPasswordResetEmail(email);
      handleOpenResetPasswordModal();
    }
  }, [handleOpenResetPasswordModal, isForgetPassword, token, email]);

  useLayoutEffect(() => {
    setTimeout(() => {
      setAnimation(true);
    }, 500);
  });

  return (
    // <GoogleReCaptchaProvider
    //   reCaptchaKey="6LcwCEsgAAAAAGH-omg2xA-5NfJOuKRX-oeE4Ubj"
    //   useRecaptchaNet
    //   scriptProps={{ async: true, defer: true, appendTo: 'body' }}
    // >
    <>
      <div
        className={cn('login', {
          login_animate: animation,
        })}
      >
        <div
          className="login__background"
          style={{
            backgroundImage: `url(${
              darkMode ? BackgroundDark : BackgroundLight
            })`,
          }}
        >
          <div className="login__modal-button" onClick={handleOpenLoginModal}>
            Log&nbsp;In
          </div>
          <div className="login__content-wrapper">
            <div className="login__content">
              <h1 className="login__title">
                <span>{title}©</span>
                <div className="login__line" />
              </h1>
              <div className="login__description">{descriptionText}</div>
              <div className="login__info">
                <div className="login__line" />
                <span>{infoText}</span>
              </div>
              <div className="login__buttons">
                <div
                  className="login__button login__button_explore"
                  onClick={handleOpenLoginModal}
                >
                  EXPLORE
                </div>
              </div>
            </div>
          </div>
          <div className="login-footer">
            <div>Powered by</div>
            <img
              src={darkMode ? WhiteshieldLogoWhite : WhiteshieldLogo}
              alt="company logo"
            />
          </div>
        </div>
      </div>
      <LoginModal
        isOpen={showLoginModal}
        onClose={handleCloseLoginModal}
        onForgotPasswordClick={handleForgotPasswordClick}
      />
      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={handleCloseForgotPasswordModal}
      />
      <ResetPasswordModal
        isOpen={showResetPasswordModal}
        onClose={handleCloseResetPasswordModal}
        token={passwordResetToken}
        email={passwordResetEmail}
      />
    </>
    // </GoogleReCaptchaProvider>
  );
};

export default Login;
